import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Fuel, ArrowRight, KeyRound, Building2, User,
  Gauge, ShieldCheck, MessageCircle, Sparkles, Users, Wallet, LineChart, CheckCircle2,
  Smartphone, Sparkle, Zap, Gift, X, CreditCard, Rocket, ChevronRight,
  ScanEye, AlertOctagon, HardDrive, FileText, ClipboardCheck, HeartHandshake,
  Lock, Download, Award,
} from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import AppFooter from "@/components/AppFooter";
import PainPointCarousel from "@/components/PainPointCarousel";
import { buildWhatsAppLink, SALES_PITCHES } from "@/lib/salesContact";
import { useEnv } from "@/components/EnvBanner";

import { useTranslation } from "react-i18next";
const FEATURES = [
  // Recently shipped (highlighted with NEW badge)
  { icon: ScanEye, title: "Nozzle OCR", desc: "Scan printer receipts — salesman readings pre-fill instantly.", isNew: true },
  { icon: ShieldCheck, title: "Bank Verify", desc: "Auto-match deposits from SMS + email bank alerts.", isNew: true },
  { icon: AlertOctagon, title: "Shift Overrides", desc: "Manager can force-close a stuck CSA shift with full audit.", isNew: true },
  { icon: Sparkles, title: "AI Insights", desc: "Daily anomalies, expense trends and safe-refuel patterns explained.", isNew: true },
  // Foundational features — curated to the four that dealers ask about most.
  { icon: Gauge, title: "Odometer-locked readings", desc: "Opening reading auto-populates from previous shift close." },
  { icon: MessageCircle, title: "WhatsApp alerts", desc: "Shortage, credit-sale receipts, and anomalies pushed instantly." },
  { icon: LineChart, title: "Nozzle continuity ledger", desc: "Chronological meter history per nozzle. Manipulation impossible." },
  { icon: Wallet, title: "3 ledgers built-in", desc: "Running cash, staff/dealer credit, and customer credit — reconciled every shift." },
];

// Industry facts intentionally removed — replaced by LiveMetricsStrip below
// that pulls real DB numbers from GET /api/public/metrics.

export default function Login() {
  const { t: __t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { login, formatApiError } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [metrics, setMetrics] = useState(null);

  // Testing Notes 12 · #6 — /demo landing page deep-links here with the
  // credentials already in the URL (e.g. /login?phone=9800000001&pin=demo1234).
  // Prefill so the sales team gets a one-tap login on production too.
  useEffect(() => {
    const params = new URLSearchParams(loc.search || "");
    const p = params.get("phone");
    const pw = params.get("pin") || params.get("password");
    if (p) setPhone(p);
    if (pw) setPassword(pw);
  }, [loc.search]);

  // Load public metrics (no auth needed)
  useEffect(() => {
    let cancelled = false;
    api.get("/public/metrics")
      .then((r) => { if (!cancelled) setMetrics(r.data); })
      .catch(() => { /* silent — landing must render even offline */ });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(phone.trim(), password);
      toast.success(__t("auth.login.welcome_back"));
      // Route based on role — DON'T follow `loc.state.from` blindly, otherwise
      // a Dealer who lands on /login from a previous CSA session gets shunted
      // straight to /csa (which used to be unrestricted). Home.jsx handles the
      // role-specific landing (CSA → /csa, Manager/Dealer → the dashboard).
      const to = u?.role === "CSA" ? "/csa" : "/";
      nav(to, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background">
      {/* Ambient background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-grid-fade opacity-60" />
      <div aria-hidden className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-16 top-10 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute bottom-0 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-emerald-500/8 blur-3xl" />

      {/* Top-right controls */}
      <div className="absolute right-4 top-14 z-20 flex items-center gap-1">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      {/* Main split-screen */}
      <main className="relative z-10 flex flex-1 items-start justify-center px-4 pb-8 pt-2 sm:pt-4">
        <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:items-start">

          {/* ============ LEFT: Marketing panel ============ */}
          <section className="hidden flex-col justify-center lg:flex enter-fade" data-testid="login-features-panel">
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/25">
                <Fuel className="h-5 w-5" />
              </div>
              <div className="font-display text-2xl font-bold tracking-tight text-foreground">{__t("components.appfooter.hisabx")}</div>
            </div>

            <h1 className="mt-8 font-display text-5xl font-bold tracking-tight text-foreground leading-[1.05] xl:text-6xl">
              Track every drop.<br />
              <span className="bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent">{__t("auth.login.trust_every_rupee")}</span>
            </h1>

            {/* ============ HERO CTA — 6-Month Free WhatsApp Chat (prominent) ============ */}
            <a
              href={buildWhatsAppLink(SALES_PITCHES.trialSignup())}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-6 flex w-full max-w-xl items-center gap-4 rounded-2xl border-2 border-emerald-500/70 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-teal-500/20 p-4 shadow-[0_12px_40px_-12px_rgba(16,185,129,0.5)] backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-500 hover:shadow-[0_16px_50px_-10px_rgba(16,185,129,0.7)] dark:from-emerald-500/25 dark:via-emerald-500/10 dark:to-teal-500/25"
              data-testid="hero-trial-badge"
            >
              <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/40">
                {/* WhatsApp glyph */}
                <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
                </svg>
                <span className="absolute -right-1 -top-1 h-3 w-3 animate-pulse rounded-full border-2 border-background bg-red-500" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-display text-xl font-black tracking-tight text-emerald-800 dark:text-emerald-100">
                    6 Months FREE
                  </span>
                </div>
                <div className="mt-0.5 text-sm font-semibold text-emerald-900/90 dark:text-emerald-100/90">
                  {__t("auth.login.chat_on_whatsapp")}
                </div>
                <div className="mt-1 text-[11px] text-emerald-800/70 dark:text-emerald-200/70">
                  No signup. One WhatsApp reply and your dealer account is live.
                </div>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-emerald-700 transition-transform group-hover:translate-x-1 dark:text-emerald-300" />
            </a>


            <p className="mt-4 max-w-lg text-base text-muted-foreground">
              The digital HISAB register built by petrol dealers, for petrol dealers.
              Shift close in 5 minutes. Zero pen-and-paper.
            </p>

            {/* ============ HERO ACTION BAND — two prominent non-tile CTAs ============ */}
            <div className="mt-7 flex flex-col gap-2.5 sm:flex-row" data-testid="hero-cta-band">
              <Link
                to="/download"
                className="group relative flex flex-1 items-center gap-3 overflow-hidden rounded-2xl border-2 border-primary/60 bg-gradient-to-br from-primary to-amber-600 px-4 py-3 text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/40"
                data-testid="hero-get-app"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30">
                  <Smartphone className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest opacity-90">Android · APK</div>
                  <div className="font-display text-base font-bold leading-tight">{__t("auth.login.get_the_app")}</div>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {/* Feature grid — recently-shipped items marked NEW, foundational features below */}
            <div className="mt-7">
              <div className="mb-2.5 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <Sparkle className="h-3 w-3 text-primary" />
                What HisabX gives your pump
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.title}
                      className={
                        "group relative overflow-hidden rounded-xl border p-3.5 transition-colors duration-200 " +
                        (f.isNew
                          ? "border-emerald-500/40 bg-emerald-500/[0.04] hover:border-emerald-500/60"
                          : "border-border/60 bg-background/40 hover:border-primary/30")
                      }
                      data-testid={`feature-tile-${i}`}
                    >
                      {f.isNew && (
                        <span
                          className="absolute right-2 top-2 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white shadow-sm"
                          data-testid={`feature-new-${i}`}
                        >
                          New
                        </span>
                      )}
                      <div className={"flex h-8 w-8 items-center justify-center rounded-lg " + (f.isNew ? "bg-emerald-500 text-white" : "bg-primary/10 text-primary")}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="mt-2 text-sm font-semibold text-foreground">{f.title}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{f.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ============ LIVE METRICS STRIP — real numbers from DB ============ */}
            <LiveMetricsStrip metrics={metrics} />

            {/* Testing Notes 12 · #7 — Data-safety trust strip. Sits above the
                subtle "Built for HPCL/IOCL/…" line so the first thing a
                skeptical dealer sees is that their books are safe. */}
            <div
              className="mt-5 rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] via-card to-emerald-500/[0.03] p-4"
              data-testid="data-safety-strip"
            >
              <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5" /> Your data safety
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {[
                  { icon: Lock,           label: "End-to-end encrypted",  hint: "TLS 1.3 + AES-256" },
                  { icon: HardDrive,      label: "Daily backups",         hint: "30-day restore" },
                  { icon: ClipboardCheck, label: "OMC audit trail",       hint: "IOCL/HPCL/BPCL ready" },
                  { icon: Download,       label: "Your data is yours",    hint: "PDF/Excel export" },
                ].map((p) => (
                  <div key={p.label} className="flex items-start gap-2" data-testid={`data-safety-${p.label.split(" ")[0].toLowerCase()}`}>
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                      <p.icon className="h-3 w-3" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold text-foreground">{p.label}</div>
                      <div className="text-[10px] text-muted-foreground">{p.hint}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2 text-[11px] text-muted-foreground">
              <div className="flex -space-x-1">
                <div className="h-6 w-6 rounded-full border-2 border-background bg-amber-400" />
                <div className="h-6 w-6 rounded-full border-2 border-background bg-emerald-500" />
                <div className="h-6 w-6 rounded-full border-2 border-background bg-sky-500" />
              </div>
              Built for BPCL · HPCL · IOCL · Nayara · Shell outlets
            </div>
          </section>

          {/* ============ RIGHT: Login card ============ */}
          <section className="flex flex-col items-center justify-start gap-3 enter-fade lg:pt-6">
            {/* Desktop-only illustrated pump scene above the login card */}
            <PumpSceneCard className="hidden w-full max-w-md lg:block" />

            <div className="w-full max-w-md">
              {/* Compact brand on mobile only */}
              <div className="mb-5 text-center lg:hidden">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[0_10px_40px_-10px_hsl(var(--primary)/0.6)]">
                  <Fuel className="h-6 w-6" />
                </div>
                <div className="font-display text-2xl font-bold tracking-tight text-foreground">{__t("components.appfooter.hisabx")}</div>
                <div className="text-[11px] text-muted-foreground">{__t("auth.login.every_drop_counted_every_rupee_tracked")}</div>
              </div>

              {/* Mobile — prominent WhatsApp CTA card above the sign-in form */}
              <a
                href={buildWhatsAppLink(SALES_PITCHES.trialSignup())}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4 flex items-center gap-3 rounded-2xl border-2 border-emerald-500/70 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-teal-500/20 p-3.5 shadow-[0_8px_28px_-10px_rgba(16,185,129,0.5)] transition-transform active:scale-[0.98] lg:hidden dark:from-emerald-500/25 dark:to-teal-500/25"
                data-testid="hero-trial-badge-mobile"
              >
                <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-md shadow-emerald-500/40">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
                  </svg>
                  <span className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-background bg-red-500" />
                </span>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-1.5">
                    <Gift className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-base font-black tracking-tight text-emerald-800 dark:text-emerald-100">
                      6 Months FREE
                    </span>
                  </div>
                  <div className="text-[11px] font-semibold text-emerald-900/90 dark:text-emerald-100/90">
                    {__t("auth.login.chat_on_whatsapp")}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-300" />
              </a>

              <div className="rounded-2xl border border-border bg-card p-7 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)]" data-testid="login-card">
                <div className="mb-5">
                  <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">{__t("auth.login.sign_in")}</h2>
                  <p className="mt-1 text-xs text-muted-foreground">Registered phone &amp; password</p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{__t("dealer.phone")}</Label>
                    <Input
                      id="phone" inputMode="numeric" value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="10-digit phone"
                      data-testid="login-phone"
                      className="h-11 text-base md:h-10 md:text-sm"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pw" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{__t("auth.login.password")}</Label>
                    <Input
                      id="pw" type="password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password"
                      className="h-11 text-base md:h-10 md:text-sm"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="group h-11 w-full text-sm font-semibold shadow-sm transition-all hover:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)] active:scale-[0.98]"
                    disabled={submitting}
                    data-testid="login-submit"
                  >
                    {submitting ? "Signing in..." : (
                      <>{__t("auth.login.sign_in")}<ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-5 border-t border-border pt-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:underline" data-testid="link-forgot-password">
                      Forgot password?
                    </Link>
                    <Link to="/register" className="text-[11px] font-semibold text-muted-foreground/80 hover:text-primary" data-testid="link-register-here">
                      New here? Sign up
                    </Link>
                  </div>

                  {/* Credit customer login shortcut — most people asking
                      "how do customers login" mean this: a fleet/individual
                      credit customer wanting to see their outstanding + get a
                      PDF statement. Their credential is phone + PIN (set up
                      via the dealer's invite or walk-in QR). */}
                  <Link
                    to="/customer"
                    className="mb-3 flex items-center justify-between rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-900 shadow-sm transition hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200"
                    data-testid="link-credit-customer"
                  >
                    <span className="inline-flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Credit customer? Login with phone + PIN
                    </span>
                    <span aria-hidden>→</span>
                  </Link>

                  <div className="mb-2 text-center text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                    Don&apos;t have an account?
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <LinkTile to="/invite" icon={<KeyRound className="h-4 w-4" />} title={__t("auth.login.invite_code")} sub="Staff/CSA" testId="link-invite" />
                    <LinkTile to="/register" icon={<Building2 className="h-4 w-4" />} title={__t("auth.login.new_dealer")} sub="Register pump" testId="link-register" />
                    <LinkTile to="/customer" icon={<User className="h-4 w-4" />} title={__t("auth.login.customer")} sub="Statements" testId="link-customer-portal" />
                  </div>
                </div>
              </div>

              {/* Mobile-only: hero CTAs (since desktop panel is hidden on mobile) */}
              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:hidden" data-testid="mobile-hero-cta">
                <Link
                  to="/download"
                  className="group flex items-center gap-2.5 rounded-xl border-2 border-primary/60 bg-gradient-to-br from-primary to-amber-600 px-3 py-2.5 text-primary-foreground shadow-md"
                  data-testid="mobile-hero-get-app"
                >
                  <Smartphone className="h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-bold uppercase tracking-wider opacity-90">{__t("auth.login.android")}</div>
                    <div className="text-sm font-bold leading-tight">{__t("auth.login.get_the_app")}</div>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                </Link>
              </div>

              {/* Mobile-only: mini features carousel below the card */}
              <div className="mt-6 flex justify-center gap-1.5 lg:hidden">
                {FEATURES.slice(0, 4).map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 rounded-full border border-border/60 bg-background/50 px-2.5 py-1 text-[10px] text-muted-foreground">
                    <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" /> {f.title}
                  </div>
                )).slice(0, 2)}
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* ============ FOUNDER STORIES CAROUSEL (moved above trial section per user request) ============ */}
      <PainPointCarousel />

      {/* ============ DEDICATED 3-MONTH FREE TRIAL SECTION ============ */}
      <section className="relative z-10 border-t border-border/60 bg-gradient-to-br from-emerald-500/5 via-background to-primary/5 px-4 py-14 sm:py-20" data-testid="trial-section">
        <div className="mx-auto max-w-6xl">
          <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-500/40 bg-card/80 p-6 shadow-[0_20px_80px_-20px_rgba(16,185,129,0.35)] backdrop-blur-xl sm:p-10 lg:p-14">
            {/* Ambient glow */}
            <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-primary/15 blur-3xl" />

            <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
              {/* Left: Big copy */}
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                  <Gift className="h-3 w-3" />
                  Limited introductory offer
                </div>
                <h2 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                  <span className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 bg-clip-text text-transparent">6 months</span> on us.
                  <br />
                  Zero credit card.
                </h2>
                <p className="mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">
                  Try HisabX end-to-end for a full quarter — every feature, every dashboard, every ledger.
                  Cancel anytime. Keep your data forever.
                </p>

                {/* Trust bullets */}
                <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                  {[
                    { icon: CreditCard, text: "No credit card asked" },
                    { icon: ShieldCheck, text: "Cancel with one click" },
                    { icon: Sparkles, text: "All features unlocked" },
                    { icon: CheckCircle2, text: "Onboarding help included" },
                  ].map((b) => {
                    const Icon = b.icon;
                    return (
                      <div key={b.text} className="flex items-center gap-2.5 text-sm text-foreground">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <span className="font-medium">{b.text}</span>
                      </div>
                    );
                  })}
                </div>

                {/* CTAs */}
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/register"
                    className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/50 active:scale-[0.98]"
                    data-testid="trial-section-cta-start"
                  >
                    <Rocket className="h-4 w-4" />
                    Start 6-month trial
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <a
                    href="mailto:hello@hisabx.in?subject=Request%20a%20HisabX%20demo"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-border bg-background/50 px-6 py-3.5 text-base font-semibold text-foreground transition hover:border-primary/50 hover:bg-primary/5"
                    data-testid="trial-section-cta-demo"
                  >
                    Talk to founder
                    <ChevronRight className="h-4 w-4" />
                  </a>
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground">
                  Trial starts the moment you sign up · No lock-in
                </div>
              </div>

              {/* Right: Big price/timeline card */}
              <div className="relative flex items-center justify-center">
                <div className="relative w-full max-w-sm rounded-2xl border-2 border-emerald-500/50 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] p-6 backdrop-blur-sm">
                  {/* Sticker */}
                  <div className="absolute -right-3 -top-3 rotate-[8deg] rounded-lg bg-red-500 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-lg ring-2 ring-background">
                    Free
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{__t("auth.login.your_first_6_months")}</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <div className="font-display text-6xl font-bold text-emerald-600 dark:text-emerald-400">₹0</div>
                    <div className="text-sm text-muted-foreground line-through">₹4,500</div>
                  </div>
                  <div className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">You save ₹4,500</div>

                  <div className="mt-5 space-y-2.5 border-t border-emerald-500/20 pt-4">
                    {[
                      "Unlimited HISABs & shifts",
                      "Manager verification hub",
                      "WhatsApp shortage alerts",
                      "Cashbook + 3 ledgers",
                      "Bulk PDF exports",
                      "Priority chat support",
                    ].map((it) => (
                      <div key={it} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                        {it}
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 rounded-xl bg-emerald-500/10 p-3 text-[11px] text-emerald-800 dark:text-emerald-200">
                    <b>{__t("auth.login.day_91")}</b> You choose. Continue on a plan that pays for itself in one shortage catch, or walk away with all your data intact.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AppFooter />

      {/* ============ FLOATING TRIAL BANNER (dismissible) ============ */}
      <FloatingTrialBanner />
    </div>
  );
}


// Compact number formatting: 1149491 → "₹11.5L", 487 → "487", 12000000 → "₹1.2Cr"
function compactINR(n) {
  const v = Number(n) || 0;
  if (v >= 10000000) return "₹" + (v / 10000000).toFixed(1).replace(/\.0$/, "") + "Cr";
  if (v >= 100000) return "₹" + (v / 100000).toFixed(1).replace(/\.0$/, "") + "L";
  if (v >= 1000) return "₹" + (v / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return "₹" + v;
}
function compactN(n) {
  const v = Number(n) || 0;
  if (v >= 100000) return (v / 100000).toFixed(1).replace(/\.0$/, "") + "L";
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(v);
}

function LiveMetricsStrip({ metrics }) {
  const m = metrics || {};
  const items = [
    { value: compactN(m.pumps), label: "pumps onboarded", color: "text-primary" },
    { value: compactN(m.staff_total), label: "staff active", color: "text-emerald-500" },
    { value: compactINR(m.sales_tracked_inr), label: "sales tracked", color: "text-sky-500" },
    { value: compactINR(m.shortages_recovered_inr), label: "shortages caught", color: "text-amber-500" },
  ];
  return (
    <div
      className="mt-7 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/5 via-background/40 to-primary/5 p-4 backdrop-blur-sm"
      data-testid="live-metrics-strip"
    >
      <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        Live across HisabX today
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        {items.map((it) => (
          <div key={it.label} className="min-w-0" data-testid={`metric-${it.label.split(" ")[0]}`}>
            <div className={"font-display text-2xl font-bold tabular-nums leading-none tracking-tight " + it.color}>
              {metrics ? it.value : "—"}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * TestimonialRotator was removed — replaced by <PainPointCarousel /> at the
 * bottom of the login page. Dealer testimonials are now dynamic (managed via
 * Super Admin → Testimonials) and render on the /pricing page.
 */



/**
 * PumpSceneCard — an illustrated petrol-pump scene rendered inline as SVG.



/**
 * PumpSceneCard — an illustrated petrol-pump scene rendered inline as SVG.
 *
 * Sits above the sign-in card on desktop. Pure CSS/SVG animations — no
 * external assets, no network, no theming quirks. Fuel drops fall from
 * the nozzle, meter digits tick, and the ground shadow subtly breathes.
 */
function LiveShiftTicker({ className = "" }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % EVENTS.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className={"relative overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-background/80 via-background/60 to-primary/[0.03] p-3 shadow-[0_6px_20px_-15px_hsl(var(--primary)/0.5)] " + className}
      data-testid="live-shift-ticker"
    >
      <div className="flex items-center gap-2">
        <span
          className="relative inline-flex h-2 w-2 shrink-0 items-center justify-center"
          aria-hidden
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
          Live · every drop
        </div>
        <div className="ml-auto text-[10px] tabular-nums text-muted-foreground">
          {EVENTS.length} shift events
        </div>
      </div>
      <div className="relative mt-2 h-10 overflow-hidden">
        {EVENTS.map((ev, idx) => (
          <div
            key={idx}
            className={
              "absolute inset-0 flex items-center gap-2 text-[13px] transition-all duration-500 " +
              (idx === i ? "translate-y-0 opacity-100" : idx === (i - 1 + EVENTS.length) % EVENTS.length ? "-translate-y-full opacity-0" : "translate-y-full opacity-0")
            }
            aria-hidden={idx !== i}
          >
            <span className={"flex h-6 w-6 shrink-0 items-center justify-center rounded-md " + ev.iconCls}>{ev.icon}</span>
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{ev.title}</span>
            <span className="shrink-0 font-mono text-xs font-bold text-primary">{ev.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const EVENTS = [
  { title: "Ravi opened Nozzle M2·N1", amount: "M2·N1", icon: <Fuel className="h-3 w-3 text-primary" />,       iconCls: "bg-primary/10" },
  { title: "Petrol dispensed",         amount: "12.4 L", icon: <Zap className="h-3 w-3 text-emerald-600" />,      iconCls: "bg-emerald-500/10" },
  { title: "Credit sale · Ashok Transport", amount: "₹8,240", icon: <ShieldCheck className="h-3 w-3 text-amber-600" />, iconCls: "bg-amber-500/10" },
  { title: "UPI received · @paytm",    amount: "₹1,050", icon: <MessageCircle className="h-3 w-3 text-sky-600" />, iconCls: "bg-sky-500/10" },
  { title: "Priya closed shift · shortage nil", amount: "0 L", icon: <ShieldCheck className="h-3 w-3 text-emerald-600" />, iconCls: "bg-emerald-500/10" },
  { title: "Bank deposit · SBI ****4021", amount: "₹42,300", icon: <ShieldCheck className="h-3 w-3 text-indigo-600" />, iconCls: "bg-indigo-500/10" },
];

function PumpSceneCard({ className = "" }) {
  const { t: __t } = useTranslation();
  return (
    <div
      className={"relative overflow-hidden rounded-2xl border border-border/70 shadow-[0_10px_40px_-20px_hsl(var(--primary)/0.35)] " + className}
      data-testid="pump-scene-card"
    >
      {/* Day-cycle sky background — 30s loop from dawn → noon → dusk → night → dawn */}
      <div aria-hidden className="pointer-events-none absolute inset-0 sky-cycle" />
      {/* Sun/moon disc that rises + sets */}
      <div aria-hidden className="pointer-events-none absolute inset-0 sun-orbit">
        <div className="sun-disc" />
      </div>
      {/* Ambient grid overlay */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-30" style={{
        backgroundImage: "linear-gradient(hsl(var(--foreground) / 0.05) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground) / 0.05) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }} />

      <div className="relative flex items-center gap-5 p-5">
        {/* SVG Illustration */}
        <PumpSVG className="h-32 w-32 shrink-0" />

        {/* Copy alongside the illustration */}
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-primary">
            <Fuel className="h-3 w-3" />
            Built for the forecourt
          </div>
          <h3 className="mt-2 font-display text-lg font-bold leading-tight text-foreground">
            The <span className="text-primary">nozzle</span> reads,
            <br />the app <span className="bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent">remembers</span>.
          </h3>
          <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
            Every litre, every rupee &mdash; captured before the CSA leaves the shift.
          </p>
        </div>
      </div>

      {/* Bottom stat strip — replaces the "Trusted at BPCL..." line (which repeats the footer) */}
      <div className="relative flex items-center justify-between gap-3 border-t border-border/60 bg-background/60 px-5 py-2.5 backdrop-blur-sm">
        <StatChip icon={<Zap className="h-3 w-3" />} label="5-min shift close" />
        <StatChip icon={<ShieldCheck className="h-3 w-3" />} label={__t("auth.login.immutable_audit")} />
        <StatChip icon={<MessageCircle className="h-3 w-3" />} label={__t("auth.login.whatsapp_alerts")} />
      </div>

      {/* Local styles — sky cycle + sun orbit + drop animations */}
      <style>{`
        .sky-cycle {
          background:
            linear-gradient(180deg,
              rgba(255, 178, 90, 0.28) 0%,
              rgba(255, 200, 130, 0.16) 30%,
              rgba(255, 220, 180, 0.06) 60%,
              transparent 100%);
          animation: skycycle 30s linear infinite;
        }
        @keyframes skycycle {
          /* Dawn (peach) */
          0%   { background: linear-gradient(180deg, rgba(255,158,80,0.32) 0%, rgba(255,196,140,0.18) 40%, transparent 100%); }
          /* Morning */
          15%  { background: linear-gradient(180deg, rgba(255,210,140,0.30) 0%, rgba(255,225,180,0.14) 45%, transparent 100%); }
          /* Noon (soft blue-cream) */
          35%  { background: linear-gradient(180deg, rgba(180,215,255,0.28) 0%, rgba(240,246,255,0.14) 50%, transparent 100%); }
          /* Golden hour */
          55%  { background: linear-gradient(180deg, rgba(255,180,90,0.32) 0%, rgba(255,158,100,0.18) 45%, transparent 100%); }
          /* Dusk (magenta-orange) */
          70%  { background: linear-gradient(180deg, rgba(230,120,150,0.30) 0%, rgba(180,80,120,0.16) 45%, transparent 100%); }
          /* Night (deep indigo) */
          85%  { background: linear-gradient(180deg, rgba(30,40,90,0.35) 0%, rgba(15,20,50,0.20) 50%, transparent 100%); }
          /* Back to dawn */
          100% { background: linear-gradient(180deg, rgba(255,158,80,0.32) 0%, rgba(255,196,140,0.18) 40%, transparent 100%); }
        }
        .sun-orbit { animation: sunorbit 30s linear infinite; }
        .sun-disc {
          position: absolute; top: 55%; left: 8%;
          width: 34px; height: 34px; border-radius: 50%;
          background: radial-gradient(circle at 40% 40%, #ffe58a, #f59e0b);
          box-shadow: 0 0 30px 4px rgba(251, 191, 36, 0.5);
          filter: blur(0.5px);
        }
        @keyframes sunorbit {
          0%   { transform: translate(0, 0);      opacity: 0.35; }  /* horizon dawn */
          25%  { transform: translate(120px, -60px);  opacity: 0.9; }  /* rising */
          50%  { transform: translate(280px, -100px); opacity: 0.95; }  /* peak */
          70%  { transform: translate(400px, -50px);  opacity: 0.75; }  /* setting */
          85%  { transform: translate(440px, 15px);   opacity: 0.15; }  /* below horizon */
          100% { transform: translate(0, 0);      opacity: 0.35; }
        }
      `}</style>
    </div>
  );
}

function StatChip({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground">
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <span>{label}</span>
    </div>
  );
}

/**
 * Custom fuel-pump illustration. Colours reference CSS theme vars so it
 * adapts to light/dark. Fuel drops fall on a repeating CSS keyframe.
 */
function PumpSVG({ className = "" }) {
  const { t: __t } = useTranslation();
  return (
    <svg viewBox="0 0 128 128" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id="pumpBody" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="hsl(var(--primary))" />
          <stop offset="1" stopColor="hsl(var(--primary) / 0.75)" />
        </linearGradient>
        <linearGradient id="fuelDrop" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fbbf24" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
        <linearGradient id="ground" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="hsl(var(--foreground) / 0)" />
          <stop offset="0.5" stopColor="hsl(var(--foreground) / 0.18)" />
          <stop offset="1" stopColor="hsl(var(--foreground) / 0)" />
        </linearGradient>
        <style>{`
          @keyframes drip {
            0%   { transform: translateY(0);    opacity: 0; }
            15%  { opacity: 1; }
            80%  { opacity: 1; }
            100% { transform: translateY(28px); opacity: 0; }
          }
          @keyframes tick {
            0%, 45% { opacity: 1; }
            50%, 95% { opacity: 0.25; }
            100% { opacity: 1; }
          }
          @keyframes breathe {
            0%, 100% { transform: scaleX(1);   opacity: 0.7; }
            50%      { transform: scaleX(1.06); opacity: 1; }
          }
          .drop-a { animation: drip 1.8s ease-in infinite; }
          .drop-b { animation: drip 1.8s ease-in infinite 0.6s; }
          .drop-c { animation: drip 1.8s ease-in infinite 1.2s; }
          .digit-tick { animation: tick 2s ease-in-out infinite; transform-origin: center; }
          .ground-line { animation: breathe 3.2s ease-in-out infinite; transform-origin: center; }
        `}</style>
      </defs>

      {/* Ground shadow line */}
      <rect x="8" y="118" width="112" height="2" rx="1" fill="url(#ground)" className="ground-line" />

      {/* Pump body — main dispenser */}
      <rect x="22" y="30" width="42" height="82" rx="4" fill="url(#pumpBody)" />
      {/* Pump top cap */}
      <rect x="20" y="26" width="46" height="7" rx="2" fill="hsl(var(--primary))" opacity="0.9" />
      {/* Screen / meter panel */}
      <rect x="27" y="38" width="32" height="20" rx="2" fill="#0b1220" />
      <rect x="27" y="38" width="32" height="20" rx="2" fill="none" stroke="hsl(var(--primary))" strokeOpacity="0.4" />
      {/* Meter digits (blinking) */}
      <g fontFamily="ui-monospace, SFMono-Regular, monospace" fontWeight="900" fontSize="9" fill="#fbbf24" className="digit-tick">
        <text x="30" y="52">₹</text>
        <text x="37" y="52">4</text>
        <text x="43" y="52">2</text>
        <text x="49" y="52">.</text>
        <text x="52" y="52">8</text>
      </g>
      {/* HISABX brand strip */}
      <rect x="27" y="62" width="32" height="5" rx="1" fill="#0b1220" />
      <text x="43" y="66" fill="#f59e0b" fontFamily="ui-sans-serif" fontSize="4" fontWeight="900" textAnchor="middle" letterSpacing="0.5">{__t("auth.login.hisabx")}</text>
      {/* Buttons */}
      <circle cx="30" cy="76" r="2" fill="hsl(var(--background) / 0.85)" />
      <circle cx="38" cy="76" r="2" fill="hsl(var(--background) / 0.85)" />
      <circle cx="46" cy="76" r="2" fill="hsl(var(--background) / 0.85)" />
      <circle cx="54" cy="76" r="2" fill="hsl(var(--background) / 0.85)" />
      {/* Bottom base */}
      <rect x="20" y="106" width="46" height="8" rx="1.5" fill="hsl(var(--primary))" opacity="0.75" />

      {/* Hose curve from pump right side to nozzle */}
      <path
        d="M 64 62 Q 82 66 90 78 Q 96 88 96 96"
        fill="none"
        stroke="#0f172a"
        strokeOpacity="0.85"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M 64 62 Q 82 66 90 78 Q 96 88 96 96"
        fill="none"
        stroke="hsl(var(--foreground) / 0.3)"
        strokeWidth="1"
        strokeLinecap="round"
      />

      {/* Nozzle — held pointing down */}
      <g>
        {/* Grip */}
        <rect x="90" y="88" width="12" height="6" rx="1.5" fill="#1f2937" />
        {/* Trigger guard */}
        <path d="M 95 94 L 95 102 Q 95 104 97 104 L 100 104 Q 102 104 102 102 L 102 94" fill="none" stroke="#374151" strokeWidth="1.2" />
        {/* Spout */}
        <rect x="92" y="94" width="8" height="14" rx="1" fill="#111827" />
        <rect x="93.5" y="106" width="5" height="3" rx="0.5" fill="#0b1220" />
        {/* Metallic highlight */}
        <rect x="92" y="94" width="1.5" height="12" rx="0.5" fill="hsl(var(--foreground) / 0.25)" />
      </g>

      {/* Fuel drops falling from spout */}
      <g>
        <ellipse cx="96" cy="112" rx="1.6" ry="2.4" fill="url(#fuelDrop)" className="drop-a" />
        <ellipse cx="96" cy="112" rx="1.6" ry="2.4" fill="url(#fuelDrop)" className="drop-b" />
        <ellipse cx="96" cy="112" rx="1.6" ry="2.4" fill="url(#fuelDrop)" className="drop-c" />
      </g>

      {/* Sparkle / accent — top-right corner of pump top */}
      <g opacity="0.9">
        <circle cx="60" cy="20" r="1.2" fill="#fbbf24" />
        <circle cx="66" cy="16" r="0.8" fill="#fbbf24" opacity="0.7" />
        <circle cx="72" cy="22" r="0.6" fill="#fbbf24" opacity="0.5" />
      </g>
    </svg>
  );
}


function LinkTile({ to, icon, title, sub, testId }) {
  return (
    <Link
      to={to}
      className="group flex flex-col items-center gap-1 rounded-xl border border-border bg-background/50 p-2.5 text-center transition hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm"
      data-testid={testId}
    >
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
        {icon}
      </div>
      <div className="text-[10px] font-semibold leading-tight text-foreground">{title}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{sub}</div>
    </Link>
  );
}

function FloatingTrialBanner() {
  const { t: __t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("hisabx_trial_banner_dismissed") === "1"; }
    catch { return false; }
  });
  const [visible, setVisible] = useState(false);
  // Delay appearance for a smoother arrival
  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [dismissed]);

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem("hisabx_trial_banner_dismissed", "1"); } catch { /* ignore */ }
  };

  if (dismissed) return null;

  return (
    <div
      className={
        "fixed bottom-4 right-4 z-50 max-w-[calc(100vw-2rem)] transition-all duration-500 sm:bottom-6 sm:right-6 " +
        (visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 pointer-events-none")
      }
      data-testid="floating-trial-banner"
    >
      <div className="relative flex items-center gap-3 overflow-hidden rounded-2xl border-2 border-emerald-500/60 bg-gradient-to-br from-emerald-500 to-emerald-700 p-3 pr-10 text-white shadow-[0_20px_60px_-10px_rgba(16,185,129,0.4)]">
        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30">
          <Gift className="h-5 w-5" />
        </div>
        <div className="relative min-w-0 flex-1">
          <div className="text-[9px] font-bold uppercase tracking-widest opacity-90">{__t("auth.login.limited_offer")}</div>
          <div className="text-sm font-bold leading-tight">6 months FREE</div>
          <div className="text-[10px] opacity-90">{__t("auth.login.chat_on_whatsapp")}</div>
        </div>
        <a
          href={buildWhatsAppLink(SALES_PITCHES.trialSignup())}
          target="_blank"
          rel="noopener noreferrer"
          className="relative inline-flex shrink-0 items-center gap-1 rounded-lg bg-white/25 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white ring-1 ring-white/30 transition-colors hover:bg-white/35"
          data-testid="floating-trial-cta"
        >
          Claim
          <ArrowRight className="h-3 w-3" />
        </a>

        <button
          onClick={close}
          aria-label={__t("auth.login.dismiss_trial_banner")}
          className="absolute right-1.5 top-1.5 rounded-md p-1 text-white/70 transition hover:bg-white/15 hover:text-white"
          data-testid="floating-trial-close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
